import {
  Divider,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  ModalOverflow,
  Step,
  StepIndicator,
  Stepper,
  Typography,
} from '@mui/joy';
import { useContext, useMemo, useState } from 'react';
import { Check } from '@mui/icons-material';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { validate as uuidValidate } from 'uuid';

import { type PhoneNumberData } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.types';

import { CaseChannel, DamageType, ExcessType, Priority } from '~/common/enums';
import { actions } from '@/reducers/ui';
import { AppContext } from '@/shared/context/context';

import useCreateRepairCase from '@/mutations/case/useCreateRepairCase';
import useDayjs from '@/shared/hooks/useDayjs';

import AddRepairCaseFormBody from './AddRepairCaseFormBody';
import AddressStep from './AddressStep';
import CaseInfoStep from './CaseInfoStep';
import DamageClaimStep from './DamageClaimStep';
import FilesStep from './FilesStep';
import InvolvedPartiesStep from './InvolvedPartiesStep';
import ManagementStep from './ManagementStep';
import PolicyStep from './PolicyStep';
import { RepairCaseIcon } from '@/shared/icons/Icons';
import SkillsStep from './SkillsStep';

type Address = {
  addressLine: string;
  city: string;
  country: string;
  postalCode: string;
};

type CompanyAddress = {
  function: string;
  address: Address;
  description: string;
  longitude: number;
  latitude: number;
};

export type FormikValues = {
  address: Address;
  admins: string[];
  buildingId: string;
  channel: string;
  classificationId: string;
  contractId: string;
  damageType: string;
  description: string;
  excessType: string;
  incidentDate: string;
  notificationDate: string;
  excessAmount: number;
  excessRemark: string;
  excessIncreasedAmount: number;
  policies: {
    policyId: string;
    policyNumber: string;
  }[];
  priority: string;
  remark: string;
  sendTo?: string;
  relations: {
    id: string;
    isRequestor: boolean;
    isClient: boolean;
    role: string;
    label: string;
    type: string;
    language: string;
    gender: string | null;
    teamId: string | null;
    companyId: string | null;
    contactId: string | null;
    firstName: string | null;
    lastName: string | null;
    phoneNumbers: PhoneNumberData[];
    remark: string | null;
    emailAddresses: string[];
    address: {
      addressLine: string;
      city: string;
      country: string;
      postalCode: string;
    } | null;
    companyName: string | null;
    vatNumber: string | null;
    companyAddresses: CompanyAddress[];
    hasMultipleOptions?: boolean;
  }[];
};

export const useSteps = () => {
  const { currentTeamUser, currentTeam } = useContext(AppContext);
  const { dayjs } = useDayjs();
  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      address: {
        addressLine: '',
        city: '',
        country: 'BE',
        postalCode: '',
      },
      admins: [currentTeamUser?.id],
      buildingId: null,
      channel: CaseChannel.Email,
      classificationId: null,
      consentOnCancellationFee: false,
      contractId: null,
      damageType: DamageType.AccidentAtWork,
      description: '',
      excessAmount: 0,
      excessIncreasedAmount: 0,
      excessRemark: '',
      excessType: ExcessType.None,
      incidentDate: dayjs().utc().format(),
      notificationDate: currentTeam?.allowRepairManagement ? dayjs().utc().format() : null,
      policies: [],
      priority: Priority.Normal,
      relations: [],
      remark: '',
      sendTo: null,
    }),
    [currentTeam?.allowRepairManagement, currentTeamUser?.id, dayjs],
  );

  const steps = useMemo(
    () => [
      { component: AddressStep, title: t('address') },
      { component: PolicyStep, title: t('excess') },
      { component: CaseInfoStep, title: t('caseInfo') },
      { component: DamageClaimStep, title: t('damageClaims') },
      { component: InvolvedPartiesStep, title: t('involvedParties') },
      { component: FilesStep, title: t('file_other') },
      currentTeam?.allowRepairManagement
        ? { component: SkillsStep, title: t('skills') }
        : { component: ManagementStep, title: t('management') },
    ],
    [currentTeam?.allowRepairManagement, t],
  );

  return useMemo(
    () => ({
      formik: {
        initialValues,
      },
      steps,
    }),
    [initialValues, steps],
  );
};

const AddRepairCaseStepper = () => {
  const { t } = useTranslation();
  const { ui, setUi } = useContext(AppContext);
  const navigate = useNavigate();

  const currentStep = ui.case.stepper.repairCase.step;

  const [redirect, setRedirect] = useState({
    toCase: true,
    toCaseInNewTab: false,
  });

  const [uiError, setUiError] = useState<string | null>(null);

  const { createRepairCaseAsync, isCreatingRepairCase } = useCreateRepairCase({ redirect: false });

  const {
    steps,
    formik: { initialValues },
  } = useSteps();

  return (
    <Modal
      open={ui.case.toggles.createRepairCase}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick') return;
        setUi({ type: actions.CASE_TOGGLE_CREATE_REPAIR_CASE });
        setUi({ payload: 1, type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP });
        setUi({ payload: [], type: actions.FILES_SET_TEMPORARY_FILES });
      }}
    >
      <ModalOverflow>
        <ModalDialog className="w-5/6">
          <ModalClose />
          <Typography startDecorator={<RepairCaseIcon />} level="title-lg">
            {t('createRepairCase')}
          </Typography>

          <Divider />

          <div className="flex grow flex-col space-y-4">
            <Stepper className="my-8">
              {steps.map((step, index) => {
                const activeStep = index + 1;
                return (
                  <Step
                    key={index}
                    orientation="vertical"
                    indicator={
                      <IconButton
                        variant="plain"
                        disabled={currentStep < activeStep}
                        onClick={() => {
                          setUi({
                            payload: activeStep,
                            type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
                          });
                          setUiError(null);
                        }}
                      >
                        <StepIndicator
                          variant={currentStep === activeStep ? 'solid' : 'soft'}
                          color={currentStep <= activeStep ? 'primary' : 'primary'}
                        >
                          {currentStep <= activeStep ? activeStep : <Check />}
                        </StepIndicator>
                      </IconButton>
                    }
                    sx={{
                      '&::after': {
                        ...(currentStep > activeStep && { bgcolor: 'primary.solidBg' }),
                      },
                    }}
                  >
                    {step.title}
                  </Step>
                );
              })}
            </Stepper>

            <Formik
              initialValues={initialValues}
              onSubmit={async (values) => {
                const updatedValues = {
                  ...values,
                  filesToAdd: ui.files.temporary,
                };

                const caseResponse = await createRepairCaseAsync(updatedValues);
                const isValidId = uuidValidate(
                  typeof caseResponse === 'string' ? caseResponse : '',
                );

                if (!isValidId) return;

                if (redirect.toCase) {
                  navigate(`/case/${caseResponse}`);
                }

                if (redirect.toCaseInNewTab) {
                  window.open(`/case/${caseResponse}`, '_blank');
                }

                setUi({ type: actions.CASE_TOGGLE_CREATE_REPAIR_CASE });
                setUi({ payload: 1, type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP });
                setUi({ payload: [], type: actions.FILES_SET_TEMPORARY_FILES });
              }}
            >
              <AddRepairCaseFormBody
                uiError={uiError}
                setUiError={setUiError}
                setRedirect={setRedirect}
                isSubmitting={isCreatingRepairCase}
                redirect={redirect}
              />
            </Formik>
          </div>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default AddRepairCaseStepper;
